import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  InputAdornment,
  Toolbar,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  SupervisorAccount as AdminIcon,
  Block as BlockIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Security as SecurityIcon,
  Groups as CommunityIcon,
  TrendingUp as SignalsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { UserProfile, UserRole, ROLE_CONFIGURATIONS, getRoleColor, getRoleDisplayName } from '../types/userTypes';
import { useRoles } from '../hooks/useRoles';
import { RoleManagementDialog } from '../components/RoleManagementDialog';

// Use the UserProfile interface from types
interface User extends UserProfile {
  // Keep any additional fields specific to the admin interface
}

export const AdminUserManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const { hasPermission, isAdmin, currentUserRole } = useRoles();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [roleDialog, setRoleDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form data for editing user
  const [editFormData, setEditFormData] = useState({
    displayName: '',
    email: '',
    mobileNumber: '',
    isAdmin: false,
    xmAffiliationVerified: false,
    xmAccountNumber: '',
    tradingExperience: '',
    preferredTradingPairs: '',
    notificationsEnabled: true,
    isBlocked: false,
  });

  // Check admin status and permissions (including legacy admin support)
  useEffect(() => {
    const checkAccess = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        // Check legacy admin status directly from Firestore for backwards compatibility
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const isLegacyAdmin = userData.isAdmin === true;
          const hasNewRoleAccess = hasPermission('user_management');
          
          // Allow access if user has new role permissions OR is a legacy admin
          if (!hasNewRoleAccess && !isLegacyAdmin) {
            setError('Access denied. User management privileges required.');
            setTimeout(() => navigate('/dashboard'), 2000);
            return;
          }
        } else {
          setError('User profile not found.');
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setError('Failed to verify admin status.');
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      }

      setLoading(false);
    };

    checkAccess();
  }, [currentUser, navigate, hasPermission]);

  // Load users
  useEffect(() => {
    const loadUsers = async () => {
      if (!currentUser) return;
      
      // Check if user has permission OR is legacy admin
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists()) return;
        
        const userData = userDoc.data();
        const isLegacyAdmin = userData.isAdmin === true;
        const hasNewRoleAccess = hasPermission('user_management');
        
        if (!hasNewRoleAccess && !isLegacyAdmin) return;
      } catch (error) {
        console.error('Error checking user permissions:', error);
        return;
      }

      try {
        setLoading(true);
        const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
        const usersSnapshot = await getDocs(usersQuery);
        const usersData: User[] = [];

        // Get all user progress data
        const userProgressQuery = collection(db, 'userProgress');
        const userProgressSnapshot = await getDocs(userProgressQuery);
        const userProgressMap = new Map();
        
        userProgressSnapshot.forEach((doc) => {
          const progressData = doc.data();
          userProgressMap.set(doc.id, progressData);
        });

        usersSnapshot.forEach((doc) => {
          const userData = doc.data() as User;
          userData.uid = doc.id;
          
          // Merge user progress data
          const progressData = userProgressMap.get(doc.id);
          if (progressData) {
            userData.currentXmLevel = progressData.currentXmLevel;
            userData.xmPoints = progressData.xmPoints;
            userData.totalCommission = progressData.totalCommission;
            userData.monthlyCommission = progressData.monthlyCommission;
          }
          
          usersData.push(userData);
        });

        setUsers(usersData);
        setFilteredUsers(usersData);
      } catch (error) {
        console.error('Error loading users:', error);
        setError('Failed to load users.');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [hasPermission, currentUser]);

  // Filter users based on search and filters
  useEffect(() => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.mobileNumber?.includes(searchTerm)
      );
    }

    // Level filter
    if (filterLevel !== 'all') {
      if (filterLevel === 'none') {
        filtered = filtered.filter(user => !user.currentXmLevel || user.currentXmLevel === 'none');
      } else {
        filtered = filtered.filter(user => user.currentXmLevel === filterLevel);
      }
    }

    // Role filter
    if (filterRole !== 'all') {
      if (filterRole === 'legacy_admin') {
        filtered = filtered.filter(user => user.isAdmin && !user.role);
      } else {
        filtered = filtered.filter(user => user.role === filterRole);
      }
    }

    // Status filter
    if (filterStatus !== 'all') {
      switch (filterStatus) {
        case 'admin':
          filtered = filtered.filter(user => user.isAdmin || (user.role && user.role !== 'user'));
          break;
        case 'verified':
          filtered = filtered.filter(user => user.xmAffiliationVerified);
          break;
        case 'blocked':
          filtered = filtered.filter(user => user.isBlocked);
          break;
        case 'unverified':
          filtered = filtered.filter(user => !user.xmAffiliationVerified);
          break;
      }
    }

    setFilteredUsers(filtered);
    setPage(0); // Reset to first page when filters change
  }, [users, searchTerm, filterLevel, filterStatus, filterRole]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const openEditDialog = () => {
    if (selectedUser) {
      setEditFormData({
        displayName: selectedUser.displayName || '',
        email: selectedUser.email || '',
        mobileNumber: selectedUser.mobileNumber || '',
        isAdmin: selectedUser.isAdmin || false,
        xmAffiliationVerified: selectedUser.xmAffiliationVerified || false,
        xmAccountNumber: selectedUser.xmAccountNumber || '',
        tradingExperience: selectedUser.tradingExperience || '',
        preferredTradingPairs: selectedUser.preferredTradingPairs || '',
        notificationsEnabled: selectedUser.notificationsEnabled !== false,
        isBlocked: selectedUser.isBlocked || false,
      });
      setEditDialog(true);
    }
    handleMenuClose();
  };

  const openRoleDialog = () => {
    setRoleDialog(true);
    handleMenuClose();
  };

  const handleUsersUpdated = () => {
    // Reload users after role change
    const loadUsers = async () => {
      if (!hasPermission('user_management')) return;

      try {
        const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
        const usersSnapshot = await getDocs(usersQuery);
        const usersData: User[] = [];

        // Get all user progress data
        const userProgressQuery = collection(db, 'userProgress');
        const userProgressSnapshot = await getDocs(userProgressQuery);
        const userProgressMap = new Map();
        
        userProgressSnapshot.forEach((doc) => {
          const progressData = doc.data();
          userProgressMap.set(doc.id, progressData);
        });

        usersSnapshot.forEach((doc) => {
          const userData = doc.data() as User;
          userData.uid = doc.id;
          
          // Merge user progress data
          const progressData = userProgressMap.get(doc.id);
          if (progressData) {
            userData.currentXmLevel = progressData.currentXmLevel;
            userData.xmPoints = progressData.xmPoints;
            userData.totalCommission = progressData.totalCommission;
            userData.monthlyCommission = progressData.monthlyCommission;
          }
          
          usersData.push(userData);
        });

        setUsers(usersData);
        setFilteredUsers(usersData);
      } catch (error) {
        console.error('Error reloading users:', error);
      }
    };

    loadUsers();
  };

  const getRoleIcon = (user: User) => {
    if (user.role) {
      switch (user.role) {
        case 'system_admin':
          return <SecurityIcon sx={{ color: ROLE_CONFIGURATIONS.system_admin.color }} />;
        case 'community_admin':
          return <CommunityIcon sx={{ color: ROLE_CONFIGURATIONS.community_admin.color }} />;
        case 'signals_admin':
          return <SignalsIcon sx={{ color: ROLE_CONFIGURATIONS.signals_admin.color }} />;
        default:
          return <PersonIcon />;
      }
    } else if (user.isAdmin) {
      return <AdminIcon color="warning" />;
    }
    return <PersonIcon />;
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    try {
      await updateDoc(doc(db, 'users', selectedUser.uid), {
        ...editFormData,
        updatedAt: serverTimestamp(),
      });

      setSuccess('User updated successfully.');
      setEditDialog(false);
      
      // Refresh users
      const updatedUsers = users.map(user => 
        user.uid === selectedUser.uid 
          ? { ...user, ...editFormData }
          : user
      );
      setUsers(updatedUsers);
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Failed to update user.');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await deleteDoc(doc(db, 'users', selectedUser.uid));
      
      setSuccess('User deleted successfully.');
      setDeleteDialog(false);
      
      // Remove user from local state
      const updatedUsers = users.filter(user => user.uid !== selectedUser.uid);
      setUsers(updatedUsers);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user.');
    }
    handleMenuClose();
  };

  const handleToggleBlock = async () => {
    if (!selectedUser) return;

    try {
      const newBlockedStatus = !selectedUser.isBlocked;
      await updateDoc(doc(db, 'users', selectedUser.uid), {
        isBlocked: newBlockedStatus,
        updatedAt: serverTimestamp(),
      });

      setSuccess(`User ${newBlockedStatus ? 'blocked' : 'unblocked'} successfully.`);
      
      // Update local state
      const updatedUsers = users.map(user => 
        user.uid === selectedUser.uid 
          ? { ...user, isBlocked: newBlockedStatus }
          : user
      );
      setUsers(updatedUsers);
    } catch (error) {
      console.error('Error toggling block status:', error);
      setError('Failed to update user status.');
    }
    handleMenuClose();
  };

  const getLevelIcon = (level?: string) => {
    switch (level) {
      case 'bronze': return '🥉';
      case 'silver': return '🥈';
      case 'gold': return '🥇';
      default: return '⭐';
    }
  };

  const getLevelColor = (level?: string) => {
    switch (level) {
      case 'bronze': return '#CD7F32';
      case 'silver': return '#C0C0C0';
      case 'gold': return '#FFD700';
      default: return '#9e9e9e';
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 4 }}>
            {error || 'Access denied. Admin privileges required.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonIcon sx={{ fontSize: 40 }} />
            User Management
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Manage user accounts, permissions, and XM loyalty levels
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 4 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Search and Filters */}
        <Card sx={{ mb: 4 }}>
          <Toolbar>
            <TextField
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mr: 2, minWidth: 300 }}
            />
            
            <FormControl sx={{ mr: 2, minWidth: 150 }}>
              <InputLabel>Level</InputLabel>
              <Select
                value={filterLevel}
                label="Level"
                onChange={(e) => setFilterLevel(e.target.value)}
              >
                <MenuItem value="all">All Levels</MenuItem>
                <MenuItem value="none">Unranked</MenuItem>
                <MenuItem value="bronze">Bronze</MenuItem>
                <MenuItem value="silver">Silver</MenuItem>
                <MenuItem value="gold">Gold</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ mr: 2, minWidth: 150 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={filterRole}
                label="Role"
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="user">Regular Users</MenuItem>
                <MenuItem value="system_admin">System Admin</MenuItem>
                <MenuItem value="community_admin">Community Admin</MenuItem>
                <MenuItem value="signals_admin">Signals Admin</MenuItem>
                <MenuItem value="legacy_admin">Legacy Admin</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="admin">Admins</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="unverified">Unverified</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />
            
            <Typography variant="body2" color="text.secondary">
              {filteredUsers.length} of {users.length} users
            </Typography>
          </Toolbar>
        </Card>

        {/* Users Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>XM Level</TableCell>
                  <TableCell>XM Points</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map((user) => (
                  <TableRow key={user.uid} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar>
                          {getRoleIcon(user)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {user.displayName || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {user.role ? (
                          <Chip
                            size="small"
                            label={getRoleDisplayName(user.role)}
                            sx={{ 
                              bgcolor: getRoleColor(user.role) + '20',
                              color: getRoleColor(user.role),
                              fontWeight: 'bold',
                            }}
                          />
                        ) : user.isAdmin ? (
                          <Chip
                            size="small"
                            label="Legacy Admin"
                            sx={{ 
                              bgcolor: '#ff9800' + '20',
                              color: '#ff9800',
                              fontWeight: 'bold',
                            }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label="User"
                            variant="outlined"
                          />
                        )}
                        {user.adminPermissions && user.adminPermissions.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            +{user.adminPermissions.length} custom
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {user.mobileNumber || 'Not provided'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          size="small"
                          label={getLevelIcon(user.currentXmLevel)}
                          sx={{ 
                            bgcolor: getLevelColor(user.currentXmLevel) + '20',
                            color: getLevelColor(user.currentXmLevel),
                          }}
                        />
                        <Typography variant="caption">
                          {user.currentXmLevel ? user.currentXmLevel.charAt(0).toUpperCase() + user.currentXmLevel.slice(1) : 'None'}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {user.xmPoints || 0}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {user.isAdmin && (
                          <Chip size="small" icon={<AdminIcon />} label="Admin" color="warning" />
                        )}
                        {user.xmAffiliationVerified && (
                          <Chip size="small" icon={<CheckIcon />} label="Verified" color="success" />
                        )}
                        {user.isBlocked && (
                          <Chip size="small" icon={<BlockIcon />} label="Blocked" color="error" />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption">
                        {user.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuClick(e, user)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Card>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={openEditDialog}>
            <EditIcon sx={{ mr: 1 }} />
            Edit User
          </MenuItem>
          <MenuItem onClick={openRoleDialog} disabled={!hasPermission('user_management')}>
            <SecurityIcon sx={{ mr: 1 }} />
            Manage Roles
          </MenuItem>
          <MenuItem onClick={handleToggleBlock}>
            {selectedUser?.isBlocked ? <CheckIcon sx={{ mr: 1 }} /> : <BlockIcon sx={{ mr: 1 }} />}
            {selectedUser?.isBlocked ? 'Unblock User' : 'Block User'}
          </MenuItem>
          <MenuItem onClick={() => setDeleteDialog(true)} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete User
          </MenuItem>
        </Menu>

        {/* Edit User Dialog */}
        <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Edit User</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Display Name"
                value={editFormData.displayName}
                onChange={(e) => setEditFormData({...editFormData, displayName: e.target.value})}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                sx={{ mb: 2 }}
                disabled
              />
              <TextField
                fullWidth
                label="Mobile Number"
                value={editFormData.mobileNumber}
                onChange={(e) => setEditFormData({...editFormData, mobileNumber: e.target.value})}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="XM Account Number"
                value={editFormData.xmAccountNumber}
                onChange={(e) => setEditFormData({...editFormData, xmAccountNumber: e.target.value})}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Trading Experience"
                value={editFormData.tradingExperience}
                onChange={(e) => setEditFormData({...editFormData, tradingExperience: e.target.value})}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Preferred Trading Pairs"
                value={editFormData.preferredTradingPairs}
                onChange={(e) => setEditFormData({...editFormData, preferredTradingPairs: e.target.value})}
                sx={{ mb: 2 }}
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.isAdmin}
                    onChange={(e) => setEditFormData({...editFormData, isAdmin: e.target.checked})}
                  />
                }
                label="Admin User"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.xmAffiliationVerified}
                    onChange={(e) => setEditFormData({...editFormData, xmAffiliationVerified: e.target.checked})}
                  />
                }
                label="XM Affiliation Verified"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.notificationsEnabled}
                    onChange={(e) => setEditFormData({...editFormData, notificationsEnabled: e.target.checked})}
                  />
                }
                label="Notifications Enabled"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.isBlocked}
                    onChange={(e) => setEditFormData({...editFormData, isBlocked: e.target.checked})}
                  />
                }
                label="Block User"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveUser} variant="contained">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete user "{selectedUser?.displayName || selectedUser?.email}"? 
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDeleteUser} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Role Management Dialog */}
        <RoleManagementDialog
          open={roleDialog}
          onClose={() => setRoleDialog(false)}
          user={selectedUser}
          onUserUpdated={handleUsersUpdated}
        />
      </Container>
    </Box>
  );
}; 